import React, { useState, useEffect, useMemo, useCallback } from 'react';
import NotificationCard from '../components/NotificationCard';
import useTokenStore from '../store/store';
import { baseUrl } from '../utils';
import useThemeStore from '../store/themeStore';
import backgroundPattern from '../images/vesta/vesta-property-pattern-01-teal-bg.svg';

interface NotificationSection {
  sectionTitle: string;
  items: string[];
}

interface Notification {
  _id: string;
  title: string;
  description: string;
  version: string;
  category: 'bug-fix' | 'feature' | 'security' | 'improvement' | 'maintenance';
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'draft' | 'published' | 'archived';
  featured: boolean;
  bannerImage?: string;
  sections?: NotificationSection[];
  tags?: string[];
  releaseDate: string | Date;
  createdAt: string | Date;
  updatedAt: string | Date;
}

interface NotificationsResponse {
  notifications: Notification[];
  featuredNotifications: Notification[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
  stats: {
    totalNotifications: number;
    publishedNotifications: number;
    draftNotifications: number;
    featuredNotifications: number;
    notificationsThisMonth: number;
    criticalUpdates: number;
    archivedNotifications: number;
    bugFixes: number;
  };
}

const Notifications: React.FC = () => {
  const getToken = useTokenStore((state) => state.token);
  const getThemes = useThemeStore((state) => state.theme);
  
  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [featuredNotifications, setFeaturedNotifications] = useState<Notification[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Computed
  const filteredNotifications = useMemo(() => {
    if (!selectedCategory) return notifications;
    return notifications.filter(n => n.category === selectedCategory);
  }, [notifications, selectedCategory]);

  const hasMorePages = useMemo(() => {
    return currentPage < totalPages;
  }, [currentPage, totalPages]);

  // Methods
  const fetchNotifications = useCallback(async (page = 1, loadMore = false) => {
    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });

      if (selectedCategory) {
        params.append('category', selectedCategory);
      }

      const url = `${baseUrl}/whitelabel/notifications?${params}`;

      const response = await fetch(url, {
        headers: {
          'x-auth-token': getToken ? (getToken as string).replace("B9I9N9456", "") : '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', errorText);
        throw new Error(`Failed to fetch notifications: ${response.status} ${errorText}`);
      }

      const data: NotificationsResponse = await response.json();
      
      if (loadMore) {
        setNotifications(prev => [...prev, ...data.notifications]);
      } else {
        setNotifications(data.notifications);
        setFeaturedNotifications(data.featuredNotifications);
      }
      
      setCurrentPage(data.pagination.page);
      setTotalPages(data.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [selectedCategory, getToken]);

  const refreshNotifications = async () => {
    setCurrentPage(1);
    await fetchNotifications(1);
  };

  const filterByCategory = async () => {
    setCurrentPage(1);
    await fetchNotifications(1);
  };

  const loadMoreNotifications = async () => {
    await fetchNotifications(currentPage + 1, true);
  };

  // Initialize
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <div className="min-h-screen flex flex-col pt-28">
      {/* Background Pattern */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-2" style={{ color: getThemes?.txtColor }}>
              Updates
            </h1>
            <p className="text-lg" style={{ color: "#000000" }}>
              Stay updated with the latest changes and improvements
            </p>
            
            {/* Refresh Button */}
            <div className="mt-6">
              <button 
                onClick={refreshNotifications}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: getThemes?.color1 || '#1A2347',
                  color: getThemes?.txtColor || '#ffffff'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = getThemes?.color2 || '#E9B54E';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = getThemes?.color1 || '#1A2347';
                }}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className={`mr-2 ${isLoading ? 'animate-spin' : ''}`}
                >
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                </svg>
                Refresh
              </button>
            </div>
          </div>
        
        {/* Featured Notifications */}
        {featuredNotifications.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#E9B54E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              Featured Updates
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredNotifications.map((notification) => (
                <NotificationCard 
                  key={notification._id}
                  notification={notification}
                  featured={true}
                  theme={getThemes}
                />
              ))}
            </div>
          </div>
        )}

        {/* All Notifications */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#1A2347]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
              All Updates
            </h2>
            
            {/* Category Filter */}
            <div className="relative">
              <select 
                value={selectedCategory} 
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  filterByCategory();
                }} 
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1A2347] focus:border-transparent transition-all duration-200 cursor-pointer"
              >
                <option value="">All Categories</option>
                <option value="bug-fix">Bug Fix</option>
                <option value="feature">Feature</option>
                <option value="security">Security</option>
                <option value="improvement">Improvement</option>
                <option value="maintenance">Maintenance</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A2347]"></div>
              <p className="mt-4 text-gray-600 font-medium">Loading notifications...</p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && filteredNotifications.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No notifications found</h3>
              <p className="text-gray-500 text-center max-w-md">Try adjusting your filters or check back later for new updates.</p>
            </div>
          )}

          {/* Notifications Grid */}
          {!isLoading && filteredNotifications.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredNotifications.map((notification) => (
                <NotificationCard 
                  key={notification._id}
                  notification={notification}
                  featured={false}
                  theme={getThemes}
                />
              ))}
            </div>
          )}

          {/* Load More Button */}
          {hasMorePages && (
            <div className="flex justify-center mt-8">
              <button 
                onClick={loadMoreNotifications}
                disabled={isLoadingMore}
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1A2347] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMore && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                )}
                Load More Notifications
              </button>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Notifications;
